"use client";

import {
  type FC,
  type Ref,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { motion } from "motion/react";
import {
  type ValueType,
  Card,
  Flex,
  Icon,
  Text,
  ValueParser,
  type ContextAction,
  ContextActions,
  Tooltip,
  iconPopupVariants,
  iconPopupTransition,
} from "@huub-web/ui";

import type { TemplateIcon } from "../../types/TemplateIcon";
import { useTemplates } from "../../hooks";
import { isEmpty } from "@huub-web/utils";
import { PageContentContext } from "../../contexts/PageContentContext";

export type ModelCardTemplateProps = {
  /**
   * The icon to display
   */
  icon?: TemplateIcon;
  /**
   * The title of the card
   */
  title?: string;
  /**
   * Whether the card is completed
   */
  completed?: boolean;
  /**
   * Optional warning message to display
   */
  warning?: string;
  /**
   * The summary data to display
   */
  summary?: {
    label: string;
    value: ValueType;
  }[];
  /**
   * The context actions to display
   */
  contextActions?: ContextAction[];
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

export const ModelCardTemplate: FC<ModelCardTemplateProps> = ({
  icon,
  title,
  completed,
  warning,
  summary,
  contextActions,
  ref,
  ...props
}) => {
  const { lockUI, registerUIContext, deregisterUIContext } = useTemplates();
  const { registerAction, deregisterAction, confirmAction } =
    useContext(PageContentContext);

  // Stable serialised keys to avoid re-running effects when summary/
  // contextActions are new (but identical) object references each render.
  const summaryKey = useMemo(
    () => (summary ? JSON.stringify(summary) : ""),
    [summary],
  );
  const contextActionsKey = useMemo(
    () =>
      contextActions ? JSON.stringify(contextActions.map((a) => a.label)) : "",
    [contextActions],
  );

  const stringifyValue = (v: ValueType): string =>
    typeof v === "object" && v !== null ? String(v.value) : String(v);

  const isNonEmptyValue = (v: ValueType | null | undefined): boolean => {
    if (v == null || v === "") return false;
    if (typeof v === "object") return v.value != null && v.value !== "";
    return true;
  };

  // Use the first non-empty summary value as differentiator so cards with
  // identical titles (e.g. "Eigen woning") get unique labels.
  const firstMeaningfulValue = useMemo(() => {
    const found = summary?.find((s) => isNonEmptyValue(s.value));
    return found ? stringifyValue(found.value) : null;
  }, [summaryKey]);
  const specificTitle = useMemo(
    () =>
      title && firstMeaningfulValue
        ? `${title} (${firstMeaningfulValue})`
        : title,
    [title, firstMeaningfulValue],
  );

  // When the user genuinely triggers a destructive context action (e.g.
  // confirms deletion via ContextActions' confirmation modal), record that
  // action's label as user-confirmed. This is the out-of-band gesture the
  // backend's trigger_page_action tool trusts (SMA-1539); the label MUST match
  // the one registered as a page action below so the backend can match it.
  const handleContextAction = useCallback(
    (action: ContextAction) => {
      if (action.type !== "delete") return;
      const label = specificTitle
        ? `${action.label}: ${specificTitle}`
        : action.label;
      confirmAction(label);
    },
    [confirmAction, specificTitle],
  );

  // Register context actions (Aanpassen, Verwijderen) for agent awareness.
  // Use the first summary value (e.g., employer name) for a specific label
  // so the agent can distinguish between multiple items of the same type.
  useEffect(() => {
    const ids: string[] = [];

    if (contextActions) {
      for (const ctxAction of contextActions) {
        if (ctxAction.action) {
          const payloadId =
            typeof ctxAction.action?.payload === "object"
              ? JSON.stringify(ctxAction.action.payload)
              : "";
          const id = `modelcard-${specificTitle}-${ctxAction.label}-${payloadId}`;
          const label = specificTitle
            ? `${ctxAction.label}: ${specificTitle}`
            : ctxAction.label;
          registerAction(id, {
            id,
            label,
            action: ctxAction.action,
            destructive: ctxAction.type === "delete",
          });
          ids.push(id);
        }
      }
    }

    return () => {
      for (const id of ids) {
        deregisterAction(id);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specificTitle, contextActionsKey, registerAction, deregisterAction]);

  const contentSliceId = `modelcard-content-${specificTitle}-${summaryKey}`;
  useEffect(() => {
    if (!specificTitle) return;
    const description = summary
      ?.filter((s) => isNonEmptyValue(s.value))
      .map((s) => `${s.label}: ${stringifyValue(s.value)}`)
      .join(", ");
    registerUIContext?.(contentSliceId, {
      type: "page",
      content: [
        {
          type: "card" as const,
          label: specificTitle,
          ...(description ? { description } : {}),
        },
      ],
    });
    return () => {
      deregisterUIContext?.(contentSliceId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    specificTitle,
    summaryKey,
    contentSliceId,
    registerUIContext,
    deregisterUIContext,
  ]);

  return (
    <Card
      ref={ref}
      padding="s"
      gap="m"
      borderRadius="xs"
      border="default"
      {...props}
    >
      <Flex direction="row" gap="xs" align="center">
        <Icon icon={icon} size="s2" />
        <Text variant="bodyM" bold grow ellipsis>
          {title}
        </Text>
        {completed && (
          <motion.div
            variants={iconPopupVariants}
            initial="initial"
            animate="animate"
            transition={iconPopupTransition}
          >
            <Tooltip value="Afgerond">
              <Icon icon="checkCircleFilled" color="gradient" />
            </Tooltip>
          </motion.div>
        )}
        {warning && (
          <motion.div
            variants={iconPopupVariants}
            initial="initial"
            animate="animate"
            transition={iconPopupTransition}
          >
            <Tooltip value={warning}>
              <Icon icon="alertFilled" color="warning">
                {warning}
              </Icon>
            </Tooltip>
          </motion.div>
        )}
      </Flex>
      {summary && summary.length > 0 && (
        <Flex gap="xxs" direction="column" grow>
          {summary.map((item, i) => (
            <Flex direction="row" gap="s" key={i} justify="space-between">
              <Text variant="bodyS" color="currentColor50" ellipsis>
                {item.label}
              </Text>
              <Text
                variant="bodyS"
                align="right"
                ellipsis
                italic={isEmpty(item.value)}
              >
                {isEmpty(item.value) ? (
                  "Onbekend"
                ) : (
                  <ValueParser value={item.value} align="right" />
                )}
              </Text>
            </Flex>
          ))}
        </Flex>
      )}
      {contextActions && contextActions.length > 0 && (
        <ContextActions
          actions={contextActions}
          title={title}
          disabled={lockUI}
          onAction={handleContextAction}
        />
      )}
    </Card>
  );
};
